/**
 * Shared base32 DID<->slug codec for room slugs.
 *
 * Used by both mutualRoom.ts (`m-<base32(did)>`) and pairRoom.ts
 * (`p-<base32(low)>-<base32(high)>`). MUST produce byte-identical output to the
 * worker's src/social/mutualSlug.js (RFC 4648 base32, lowercase, no padding,
 * over the DID's UTF-8 bytes), since the worker recomputes/decodes these slugs
 * to gate room admission.
 */

const ALPHABET = 'abcdefghijklmnopqrstuvwxyz234567'

/** Decodes a base32 body back to a DID string, or null if malformed / not a DID. */
export function decodeDidBase32(b32: string): string | null {
  if (!b32.length) return null
  let bits = 0
  let value = 0
  const bytes: number[] = []
  for (let i = 0; i < b32.length; i++) {
    const idx = ALPHABET.indexOf(b32[i])
    if (idx === -1) return null
    value = (value << 5) | idx
    bits += 5
    if (bits >= 8) {
      bytes.push((value >>> (bits - 8)) & 0xff)
      bits -= 8
    }
  }
  try {
    const did = new TextDecoder().decode(new Uint8Array(bytes))
    return did.startsWith('did:') ? did : null
  } catch {
    return null
  }
}

/** base32-encodes the UTF-8 bytes of a DID string (no prefix, no padding). */
export function encodeDidBase32(did: string): string {
  const bytes = new TextEncoder().encode(did)
  let bits = 0
  let value = 0
  let out = ''
  for (let i = 0; i < bytes.length; i++) {
    value = (value << 8) | bytes[i]
    bits += 8
    while (bits >= 5) {
      out += ALPHABET[(value >>> (bits - 5)) & 31]
      bits -= 5
    }
  }
  if (bits > 0) {
    out += ALPHABET[(value << (5 - bits)) & 31]
  }
  return out
}
